import PropTypes from "prop-types";
import { forwardRef, useEffect, useMemo, useRef } from "react";

import styled, { css } from "styled-components/macro";

import { useNetworkMetadata } from "Flocknote/apiHooks";

import FnrSelectList2 from "UI/FnrSelectList2";
import FnrTappyShrink from "UI/FnrTappyShrink";
import { colors } from "UI/colors";

import { COUNTRY_OPTIONS } from "Utilities/Address/networkCountryOptions";

import { useAddress } from "./AddressContext";

CountryField.propTypes = {
  // country ID or country code abbreviation
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  onMenuChange: PropTypes.func,
};

export function CountryField({ value, onChange, onMenuChange }) {
  const [networkMetadata] = useNetworkMetadata();
  const networkCountryCode = networkMetadata?.address?.countryCode;

  const { clearStateField } = useAddress();

  let countryIDOrCode = value;
  // try as a proper ID (e.g. `1`) first, then try as a "countryCode" (e.g. 'US'),
  // since we aren't guaranteed one particular strategy or the other from the backend
  const selectedCountry =
    COUNTRY_OPTIONS.find(
      ({ ID, countryCode }) =>
        ID === countryIDOrCode || countryCode === countryIDOrCode
    ) ??
    // default to the network's country
    COUNTRY_OPTIONS.find((c) => c.countryCode === networkCountryCode);

  // accommodate both value strategies as well as defaulting to the network's country
  countryIDOrCode ||= selectedCountry?.ID;

  // Push the resolved default up to the parent once, so parent state matches
  // the displayed flag. Without this, a parent that passes `value={undefined}`
  // sees a flag but never receives the country, leaving downstream consumers
  // (e.g. address verification) blocked on a country they thought was set.
  const hasPushedDefaultRef = useRef(false);
  const resolvedDefaultID = !value ? selectedCountry?.ID : null;
  useEffect(() => {
    if (!hasPushedDefaultRef.current && resolvedDefaultID && onChange) {
      hasPushedDefaultRef.current = true;
      onChange(resolvedDefaultID);
    }
  }, [resolvedDefaultID, onChange]);

  // Each option leads with its flag (the slot the inline check used to hold;
  // the selection check renders on the right instead).
  const options = useMemo(
    () =>
      COUNTRY_OPTIONS.map(({ ID, name, flag }) => ({
        id: ID,
        label: name,
        leading: flag,
      })),
    []
  );

  const selectedOption = selectedCountry
    ? { id: selectedCountry.ID, label: selectedCountry.name }
    : null;

  const handleSelect = ({ id }) => {
    if (id !== countryIDOrCode) {
      onChange(id);
      clearStateField();
    }
  };

  return (
    <Wrapper className="country-field">
      <FnrSelectList2
        name="country"
        // The flag nests in the street input's corner, so a keyboard Tab stop
        // here interrupts the natural Street → City flow. Drop it from the Tab
        // sequence; it stays pointer-clickable (and programmatically focusable,
        // so Space still opens the menu once reached).
        triggerTabIndex={-1}
        ButtonComponent={FlagTrigger}
        throttleProps
        optionStyle="popoverOption"
        options={options}
        selected={selectedOption}
        onSelect={handleSelect}
        onOpenDropdown={() => onMenuChange?.(true)}
        onCloseDropdown={() => onMenuChange?.(false)}
        flag={selectedCountry?.flag || "🇺🇸"}
      />
    </Wrapper>
  );
}

const FlagTrigger = forwardRef(function FlagTrigger({ flag, ...props }, ref) {
  return (
    <Flag data-cy="FnrAddress_ContryFieldButton" ref={ref} {...props}>
      {flag}
    </Flag>
  );
});

FlagTrigger.propTypes = {
  flag: PropTypes.string,
};

// Subtle grey fill + darker grey border. Shared by hover and keyboard focus so
// the flag highlights identically however it's reached — and it IS the flag's
// focus indicator now (we drop the UA outline / FnrButtonWrapper tint below).
const greyHighlight = css`
  background: ${colors.fnGray100};
  border-color: ${colors.fnGray300};
`;

// FnrTappyShrink gives the flex centering + the press-shrink (whileTap) tactile
// feedback; we add the glyph size, the fixed hit area, and the highlight.
//
// The 28×28 box IS the clickable hit area. A fixed square (rather than padding
// around the glyph) keeps the target identical across surfaces and independent
// of per-flag glyph width. It lives here on the flag — which sits inside
// FnrSelectList2's chromeless (padding:0) <button> — so the button hugs the box
// and its full area is the click target.
const Flag = styled(FnrTappyShrink)`
  width: 28px;
  height: 28px;
  box-sizing: border-box;
  font-size: 20px;
  border-radius: 5px;
  /* Transparent at rest so the hover/focus border never shifts layout. */
  border: 1px solid transparent;
  transition: background-color 200ms ease, border-color 200ms ease;

  &:hover {
    ${greyHighlight}
  }
`;

// The flag is a compact inline control that nests inside the street input's
// right corner (positioned by each consumer). It is no longer the full-height
// bordered left column the address card once led with.
const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: #222;
  cursor: pointer;

  /* FnrSelectList2 wraps the trigger in its own <button>; keep it chromeless
     and unpadded so it hugs the flag (which owns the 28×28 hit area below). */
  & > button {
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
  }

  /* The <button> is the focus target, not the flag — a keyboard tab focuses it,
     so the flag's own :focus-visible never fires. Cancel FnrButtonWrapper's
     default focus tint (a flat rgba grey, no border) and route the highlight to
     the flag instead, so keyboard focus matches hover exactly. */
  & > button:focus-visible {
    outline: none;
    background-color: transparent;
  }

  & > button:focus-visible ${Flag} {
    ${greyHighlight}
  }
`;
